import { Music } from 'lucide-react'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/cn'

export type CoverSize = 'sm' | 'md' | 'lg' | 'xl'

export interface CoverProps {
  /** Null for a track with no artwork, or before a download has finished. */
  src?: string | null
  /**
   * Leave empty when the title is already rendered beside the image — a
   * duplicate announcement is worse than none.
   */
  alt?: string
  size?: CoverSize
  className?: string
}

/** Fixed boxes, so a slow or missing image never reflows the row around it. */
const SIZES: Record<CoverSize, string> = {
  sm: 'w-10 rounded-md',
  md: 'w-12 rounded-md',
  lg: 'w-24 rounded-lg',
  xl: 'w-full rounded-card',
}

const ICON_SIZES: Record<CoverSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-8 w-8',
  xl: 'h-1/4 w-1/4',
}

export function Cover({ src, alt = '', size = 'sm', className }: CoverProps) {
  const [failed, setFailed] = useState(false)

  // A recycled row can point at a different cover; the previous failure must not
  // stick to the new one.
  useEffect(() => {
    setFailed(false)
  }, [src])

  const showPlaceholder = !src || failed

  return (
    // A span rather than a div: covers are routinely rendered inside the
    // full-row play button, and a button may only contain phrasing content.
    <span
      className={cn(
        'relative block aspect-square shrink-0 overflow-hidden bg-surface-hover',
        SIZES[size],
        className,
      )}
    >
      {showPlaceholder ? (
        <span className="flex h-full w-full items-center justify-center" aria-hidden="true">
          <Music className={cn('text-text-subtle', ICON_SIZES[size])} />
        </span>
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      )}
    </span>
  )
}
